"use client"

import { ArrowRight, Users, Calendar, MapPin, Clock } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import Link from "next/link"

const featuredLeagues = [
  {
    id: "val-northeast-tue",
    game: "Valorant",
    name: "Northeast Tactical",
    day: "Tuesdays",
    time: "9:00 PM ET",
    region: "Northeast",
    spotsLeft: 4,
    totalSpots: 24,
    startsIn: "5 days",
    tags: ["Competitive", "Voice Required"],
  },
  {
    id: "rl-midwest-thu",
    game: "Rocket League",
    name: "Midwest Aerials",
    day: "Thursdays",
    time: "8:30 PM CT",
    region: "Midwest",
    spotsLeft: 8,
    totalSpots: 18,
    startsIn: "12 days",
    tags: ["All Skill Levels"],
  },
  {
    id: "apex-pacific-sat",
    game: "Apex Legends",
    name: "Pacific Predators",
    day: "Saturdays",
    time: "7:00 PM PT",
    region: "Pacific Northwest",
    spotsLeft: 2,
    totalSpots: 20,
    startsIn: "3 days",
    tags: ["Competitive", "Almost Full"],
  },
  {
    id: "cod-southeast-fri",
    game: "Call of Duty: Warzone",
    name: "Southern Operators",
    day: "Fridays",
    time: "9:00 PM ET",
    region: "Southeast",
    spotsLeft: 12,
    totalSpots: 24,
    startsIn: "8 days",
    tags: ["Casual Competitive"],
  },
  {
    id: "sports-night-national",
    game: "Sports Night",
    name: "National Sports Rotation",
    day: "Sundays",
    time: "8:00 PM ET",
    region: "National",
    spotsLeft: 16,
    totalSpots: 32,
    startsIn: "10 days",
    tags: ["Multi-Game", "FIFA/Madden/NBA2K"],
  },
  {
    id: "fps-sunday-mountain",
    game: "FPS Sundays",
    name: "Mountain Time Shooters",
    day: "Sundays",
    time: "7:00 PM MT",
    region: "Mountain",
    spotsLeft: 6,
    totalSpots: 20,
    startsIn: "10 days",
    tags: ["Multi-Game", "Rotating FPS"],
  },
]

export function FeaturedLeagues() {
  return (
    <section className="py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              Leagues Starting Soon
            </h2>
            <p className="mt-2 text-muted-foreground">
              Season 12 registration closes in 2 weeks
            </p>
          </div>
          <Link href="/leagues">
            <Button variant="ghost" className="gap-2 text-primary hover:text-primary/80">
              View All Leagues
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {featuredLeagues.map((league) => {
            const almostFull = league.spotsLeft <= 4
            
            return (
              <Card 
                key={league.id}
                className="group border-border hover:border-primary/50 transition-all duration-300 overflow-hidden"
              >
                <CardContent className="p-0">
                  {/* Header with game name */}
                  <div className="bg-secondary/50 px-4 py-3 border-b border-border flex items-center justify-between">
                    <span className="font-semibold text-foreground">{league.game}</span>
                    {almostFull && (
                      <span className="text-xs bg-accent/20 text-accent px-2 py-0.5 rounded-full">
                        {league.spotsLeft} spots left
                      </span>
                    )}
                  </div>
                  
                  <div className="p-4">
                    <h3 className="font-bold text-lg text-foreground mb-3">{league.name}</h3>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Calendar className="h-4 w-4 text-primary" />
                        <span>{league.day} @ {league.time}</span>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <MapPin className="h-4 w-4 text-primary" />
                        <span>{league.region}</span>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Users className="h-4 w-4 text-primary" />
                        <span>{league.spotsLeft} of {league.totalSpots} spots open</span>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Clock className="h-4 w-4 text-primary" />
                        <span>Starts in {league.startsIn}</span>
                      </div>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mt-4">
                      {league.tags.map((tag) => (
                        <span 
                          key={tag}
                          className="text-xs bg-secondary text-muted-foreground px-2 py-1 rounded"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    <Button 
                      className="w-full mt-4 bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
                      asChild
                    >
                      <Link href={`/leagues/${league.id}`}>
                        View League Details
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      </div>
    </section>
  )
}
